import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, X, PartyPopper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// One-time celebratory banner for the first deploy Andy shipped from the VPS.
// Self-contained + localStorage-gated (per-user, falls back to global) so it
// shows once per browser — which means it also greets him fresh on the iPhone
// after logging in with Face ID. Bump the _v suffix to re-show it to everyone.
const STORAGE_KEY = 'vps_first_deploy_seen_v1';

export default function VpsDeployBanner() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const key = user?.id ? `${STORAGE_KEY}__${user.id}` : STORAGE_KEY;

  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(key); } catch { return false; }
  });

  if (dismissed) return null;

  const close = () => {
    try { localStorage.setItem(key, Date.now().toString()); } catch { /* ignore */ }
    setDismissed(true);
  };

  const firstName = (user?.name || '').trim().split(/\s+/)[0];

  return (
    <div
      className="card animate-in"
      style={{
        margin: '0 0 16px 0',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #26ABE3',
        background: 'linear-gradient(135deg, rgba(41,171,226,0.14), rgba(35,181,166,0.10))',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 52,
          height: 52,
          borderRadius: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #29ABE2, #1488A6)',
          color: '#fff',
          boxShadow: '0 4px 14px rgba(20,136,166,0.4)',
        }}
      >
        <Rocket size={26} />
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--c-text)' }}>
            {firstName
              ? t('vpsDeploy.titleNamed', { name: firstName, defaultValue: '¡{{name}}, funcionó! 🎉' })
              : t('vpsDeploy.title', '¡Funcionó! 🎉')}
          </span>
          <PartyPopper size={18} style={{ color: '#23B5A6' }} />
        </div>
        <div style={{ fontSize: 14, color: 'var(--c-text-muted)', marginTop: 4, lineHeight: 1.5 }}>
          {t(
            'vpsDeploy.body',
            'Bienvenido al primer deploy que hiciste desde tu VPS. Este cartelito viajó de tu servidor a producción, y vos llegaste hasta acá con el link del Telegram y tu Face ID. Todo el circuito cerró. 🚀'
          )}
        </div>
      </div>

      <button
        onClick={close}
        className="btn btn-ghost btn-icon btn-sm"
        title={t('common.dismiss', 'Descartar')}
        style={{ flexShrink: 0, alignSelf: 'flex-start' }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
